import {
  aportesLegalesConstruccion,
  calcularRemuneracionPeriodoConstruccion,
  resolverNetoPorDiaConstruccion,
  resolverRegistroAfpEnLista,
} from './calculoRemuneracionConstruccionCivil';
import construccionCivilService from './construccionCivilService';
import { resolverPeriodoCalculoConsu } from '../utils/periodoCalculoConsu';

function afpRegistroDeEmpleado(empleado) {
  const afps = empleado.datosAPIs?.afps || [];
  const nombre = empleado.datosSalariales?.afp
    || empleado.datosEmpleado?.afp?.nombre
    || empleado.afp?.nombre;
  return resolverRegistroAfpEnLista(nombre, afps)
    || (typeof empleado.datosEmpleado?.afp === 'object' ? empleado.datosEmpleado.afp : null);
}

function resolverFichaYParametro(empleado, datosDisponibles, contexto) {
  const consu = empleado.datosAPIs?.construccionCivil
    || contexto?.contexto?.construccionCivil
    || {};
  const fichas = consu.fichas || [];
  const ficha = consu.ficha
    || empleado.datosEmpleado?.construccion
    || fichas.find((f) => Number(f.empleado_id) === Number(empleado.empleado_id));
  const fecha = datosDisponibles.analisisPeriodo?.fechaInicio
    || contexto?.contexto?.planilla_real?.fecha_inicio;
  const parametros = consu.parametros || [];
  const categoria = ficha?.categoria;
  const param = consu.parametro
    || parametros.find((p) => {
      if (String(p.categoria) !== String(categoria)) return false;
      const desde = String(p.vigente_desde || '').slice(0, 10);
      const hasta = p.vigente_hasta ? String(p.vigente_hasta).slice(0, 10) : '9999-12-31';
      const dia = String(fecha || '').slice(0, 10);
      return dia >= desde && dia <= hasta;
    });
  const plame = {};
  (consu.plame || []).forEach((row) => {
    plame[row.concepto_clave] = row;
  });
  return {
    ficha,
    param,
    plame,
    derechos: consu.derechos || ficha?.derechos || [],
    conceptosPlame: consu.conceptosPlame || empleado?.datosAPIs?.conceptosPlame || [],
  };
}

export async function aplicarLogicaPlanillaConstruccion(empleado, contexto, datosDisponibles) {
  const { ficha, param, plame, derechos, conceptosPlame } = resolverFichaYParametro(empleado, datosDisponibles, contexto);
  const planilla = contexto.contexto?.planilla_real || {};
  const tareos = datosDisponibles.tareos?.tareosDetallados
    || empleado.tareosEmpleadoFiltrados
    || empleado.tareosEmpleado
    || [];

  const hijos = (ficha?.empleado?.hijos || empleado.datosEmpleado?.hijos || []).filter((h) => {
    if (!h.acredita_estudios) return false;
    const fecha = String(planilla.fecha_fin || planilla.fecha_inicio || '').slice(0, 10);
    const hasta = h.estudios_vigente_hasta ? String(h.estudios_vigente_hasta).slice(0, 10) : null;
    const desde = h.estudios_vigente_desde ? String(h.estudios_vigente_desde).slice(0, 10) : null;
    if (desde && fecha < desde) return false;
    if (hasta && fecha > hasta) return false;
    return true;
  });

  const periodoGratificacion = empleado.periodo_gratificacion_consu
    || empleado.datosSalariales?.periodoGratificacionConsu
    || empleado.datosEmpleado?.periodo_gratificacion_consu
    || null;

  const periodoCalculo = resolverPeriodoCalculoConsu({
    planilla,
    fechaCalculoDesde: empleado.fecha_calculo_desde
      || empleado.datosSalariales?.fechaCalculoDesde
      || empleado.datosEmpleado?.fecha_calculo_desde,
    fechaCalculoHasta: empleado.fecha_calculo_hasta
      || empleado.datosSalariales?.fechaCalculoHasta
      || empleado.datosEmpleado?.fecha_calculo_hasta,
  });

  const calculo = calcularRemuneracionPeriodoConstruccion({
    fechaInicio: periodoCalculo.fechaInicio
      || planilla.fecha_inicio
      || datosDisponibles.analisisPeriodo?.fechaInicio,
    fechaFin: periodoCalculo.fechaFin
      || planilla.fecha_fin
      || datosDisponibles.analisisPeriodo?.fechaFin,
    parametro: param,
    ficha,
    tareos,
    feriados: empleado.datosAPIs?.feriadosPagoDoble || [],
    hijosConDerecho: hijos.length,
    derechosPrevios: derechos,
    plame,
    conceptosPlame,
    fechaIngreso: datosDisponibles.historialLaboral?.fechaIngresoActual,
    fechaCese: datosDisponibles.historialLaboral?.fechaCeseActual,
    periodoGratificacion,
  });

  if (calculo && typeof calculo === 'object') {
    calculo.periodoCalculo = periodoCalculo;
    if (calculo.resumen) {
      calculo.resumen.fechaInicio = periodoCalculo.fechaInicio;
      calculo.resumen.fechaFin = periodoCalculo.fechaFin;
      calculo.resumen.overridePeriodo = periodoCalculo.override;
    }
  }

  if (calculo.error) {
    return {
      ...empleado,
      errorCalculo: calculo.error,
      calculoConstruccionCivil: calculo,
      procesamiento: {
        timestamp: new Date().toISOString(),
        estado: 'error_construccion_civil',
        observaciones: calculo.error,
      },
    };
  }

  const legales = aportesLegalesConstruccion({
    bases: calculo.bases,
    parametro: param,
    sistemaPensionario: empleado.datosSalariales?.sistemaPensionario
      || empleado.datosEmpleado?.sistema_pensionario,
    afpRegistro: afpRegistroDeEmpleado(empleado),
    tipoAfp: empleado.datosSalariales?.tipoAfp || empleado.datosEmpleado?.tipo_afp,
    fechaNacimiento: empleado.datosEmpleado?.fechaNacimiento
      || empleado.datosEmpleado?.fecha_nacimiento,
    plame,
    conceptosPlame,
  });

  const conceptosUsuario = {
    ingresos: calculo.ingresos,
    descuentos: [...calculo.descuentos, ...legales.descuentos],
    aportes: [...calculo.aportes, ...legales.aportes],
  };
  const totalIngresos = conceptosUsuario.ingresos.reduce((s, i) => s + Number(i.monto || 0), 0);
  const totalDescuentos = conceptosUsuario.descuentos.reduce((s, i) => s + Number(i.monto || 0), 0);
  const totalAportes = conceptosUsuario.aportes.reduce((s, i) => s + Number(i.monto || 0), 0);
  const netoAPagar = totalIngresos - totalDescuentos;
  const { netoPorDia, diasNeto } = resolverNetoPorDiaConstruccion({
    neto: netoAPagar,
    diasAsistencia: calculo.resumen?.diasAsistencia,
    diasComputables: calculo.resumen?.diasComputables,
  });
  const totales = {
    totalIngresos,
    totalDescuentos,
    totalAportes,
    netoAPagar,
    netoPorDia,
    diasNeto,
  };

  const calculosBoletaService = require('./calculosBoletaService').default;
  const regimenCompleto = (empleado.datosAPIs?.regimenesLaborales || []).find(
    (r) => r.codigo === empleado.datosSalariales?.regimenLaboral
      || r.nombre === empleado.datosSalariales?.regimenLaboral
  );

  const datosCompletos = {
    datosEmpresa: contexto.contexto?.empresa,
    datosAPIs: empleado.datosAPIs,
    datosSalariales: {
      ...empleado.datosSalariales,
      tipoContratacion: 'Planilla',
      regimenLaboral: regimenCompleto?.nombre || empleado.datosSalariales?.regimenLaboral,
    },
    conceptosUsuario,
    datosEmpleado: empleado.datosEmpleado,
    periodo: contexto.contexto?.periodo,
    planilla: contexto.contexto?.planilla_real,
    construccionCivil: {
      bases: calculo.bases,
      parametro: param,
      detalleDias: calculo.detalleDias,
    },
  };

  const resultadoBoleta = await calculosBoletaService.generarCalculos(
    datosCompletos,
    'planilla_construccion_civil'
  );

  const empresaId = contexto.contexto?.empresa?.id || empleado.datosEmpresa?.id;
  if (empresaId && calculo.derechos?.length) {
    await construccionCivilService.registrarDerechos(
      empresaId,
      calculo.derechos.map((d) => ({
        ...d,
        empleado_id: empleado.empleado_id,
        planilla_id: planilla.id || null,
        fecha_inicio: planilla.fecha_inicio,
        fecha_fin: planilla.fecha_fin,
      }))
    );
  }

  return {
    ...empleado,
    conceptosUsuario,
    totales,
    boleta: resultadoBoleta,
    calculoConstruccionCivil: calculo,
    derechosConstruccion: calculo.derechos,
    procesamiento: {
      timestamp: new Date().toISOString(),
      estado: 'procesado_construccion_civil',
      observaciones: 'Cálculo diario CONSU (no usa /30)',
      calculosRealizados: {
        totalIngresos: totales.totalIngresos,
        totalDescuentos: totales.totalDescuentos,
        totalAportes: totales.totalAportes,
        netoAPagar: totales.totalIngresos - totales.totalDescuentos,
      },
    },
  };
}
